import { InternalLink } from "./InternalLinks";

interface ContentBlockProps {
  title: string;
  content: string;
  links?: Array<{ text: string; to: string }>;
  keywords?: string[];
}

// SEO-optimized content blocks with internal linking
export const ContentBlock = ({ title, content, links, keywords }: ContentBlockProps) => {
  return (
    <div className="prose max-w-none">
      <h2 className="text-2xl font-bold text-foreground mb-4">{title}</h2>
      
      <div className="text-muted-foreground leading-relaxed mb-4">
        {content}
      </div>

      {links && links.length > 0 && (
        <div className="flex flex-wrap gap-4 mt-4">
          {links.map((link, index) => (
            <InternalLink key={index} to={link.to} showArrow>
              {link.text}
            </InternalLink>
          ))}
        </div>
      )}

      {keywords && keywords.length > 0 && (
        <div className="mt-4 text-xs text-muted-foreground">
          <strong>Keywords:</strong> {keywords.join(', ')}
        </div>
      )}
    </div>
  );
};

// SEO-rich contextual content for product pages
export const ProductContextBlock = ({ category }: { category: string }) => {
  const contexts = {
    'Human APIs': {
      title: 'Premium Human Pharmaceutical Active Ingredients',
      content: `Our human pharmaceutical APIs are manufactured under strict GMP conditions with full regulatory compliance for FDA, EMA, and other international health authorities. Each active pharmaceutical ingredient undergoes rigorous quality testing and comes with complete documentation including Certificate of Analysis (COA), Safety Data Sheets (SDS), and regulatory support for DMF/CEP submissions. We serve pharmaceutical manufacturers across North America, Europe, and South America with reliable supply chains and competitive pricing for both standard and custom API synthesis.`,
      links: [
        { text: 'View All Human APIs', to: '/products/human-apis' },
        { text: 'Quality Certifications', to: '/quality' },
        { text: 'Request Custom Quote', to: '/contact' }
      ],
      keywords: ['pharmaceutical APIs', 'FDA approved APIs', 'GMP manufacturing', 'human medicine APIs', 'regulatory compliance', 'pharmaceutical manufacturers']
    },
    'Intermediates': {
      title: 'High-Quality Pharmaceutical Intermediates & Building Blocks',
      content: `Our pharmaceutical intermediates are the building blocks downstream API synthesis runs on, manufactured to tight purity and consistency targets. Technical support, analytical data, and scale-up services ship with each batch. From early-stage drug discovery through commercial manufacturing, the intermediates meet the specifications most pharmaceutical companies file against. ISO 9001:2015 processes keep batch-to-batch consistency within audit-tolerable limits.`,
      links: [
        { text: 'Browse Intermediates Catalog', to: '/products/intermediates' },
        { text: 'Custom Synthesis Services', to: '/capabilities' },
        { text: 'Technical Support', to: '/contact' }
      ],
      keywords: ['pharmaceutical intermediates', 'drug synthesis', 'chemical building blocks', 'custom synthesis', 'pharmaceutical research', 'API precursors']
    },
    'Veterinary APIs': {
      title: 'Specialized Veterinary Active Pharmaceutical Ingredients',
      content: `Our veterinary APIs cover animal-health applications — antiparasitics, antibiotics, and therapeutic compounds — for the global veterinary pharma market. Each veterinary API is manufactured to the same standards as our human-grade material, so the efficacy and safety profile transfers cleanly. Regulatory support and documentation ship for veterinary drug manufacturers across every major market.`,
      links: [
        { text: 'Veterinary API Products', to: '/products/veterinary' },
        { text: 'Animal Health Solutions', to: '/capabilities' },
        { text: 'Get Veterinary Quote', to: '/contact' }
      ],
      keywords: ['veterinary APIs', 'animal health pharmaceuticals', 'veterinary medicine', 'animal pharmaceuticals', 'veterinary drugs', 'livestock health']
    }
  };

  const context = contexts[category as keyof typeof contexts];
  if (!context) return null;

  return (
    <div className="bg-muted/20 p-8 rounded-lg my-8">
      <ContentBlock {...context} />
    </div>
  );
};

// Industry-specific content blocks for SEO
export const IndustryInsights = () => {
  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12">Pharmaceutical Manufacturing Excellence</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          <ContentBlock
            title="FDA & EMA Regulatory Compliance"
            content="Longshine Boi-Tech files with FDA (US), EMA (Europe), Health Canada, ANVISA (Brazil), and COFEPRIS (Mexico). Our APIs ship with full regulatory support — DMF (Drug Master File) submissions, CEP (Certificate of Suitability to European Pharmacopoeia), and the rest of the documentation set buyers need to file in their own markets. Approval workflows run end-to-end for clients across North America, South America, and Europe."
            links={[
              { text: 'View Quality Standards', to: '/quality' },
              { text: 'Download Certifications', to: '/about' }
            ]}
            keywords={['FDA compliance', 'EMA approval', 'pharmaceutical regulations', 'DMF', 'CEP', 'regulatory support']}
          />

          <ContentBlock
            title="ISO 9001:2015 Certified Manufacturing"
            content="Our facility in Pune, Maharashtra runs under ISO 9001:2015 with a WHO-GMP QMS. Every lot is released only after HPLC identity + assay, GC for residual solvents (per ICH Q3C), FT-IR structural confirmation, and microbial testing against USP <61>/<62>. Buyers get the release records in the CoA plus a batch-manufacturing record on request. We ship to 50+ countries and welcome on-site or remote audits — most European and US customers audit us once at onboarding, then annually."
            links={[
              { text: 'Manufacturing Capabilities', to: '/capabilities' },
              { text: 'Request Facility Audit', to: '/contact' }
            ]}
            keywords={['ISO 9001', 'GMP manufacturing', 'quality control', 'pharmaceutical testing', 'India manufacturer', 'facility audit']}
          />
        </div>
      </div>
    </section>
  );
};
